import { isVODStatusFailed } from '@/hooks/useTrailerViewMedia'
import { createPlaybackToken } from '@/server/services/blendvision'
import { getVod } from '@/services/blendvision'
import type { TrailerViewVideoData } from '@/types/asset'
import useSWR from 'swr'

const POLLING_INTERVAL_MS = 10000

const fetchVodStatus = async (videos: TrailerViewVideoData[]) => {
  return Promise.all(
    videos.map(async (video) => {
      if (!video.vodId || video.isConverted || isVODStatusFailed(video.vodStatus)) return video

      const vodRes = await getVod(video.vodId)
      const vod = vodRes && 'vod' in vodRes ? vodRes.vod : undefined

      if (!vod || isVODStatusFailed(vod.status)) {
        return {
          ...video,
          isConverted: false,
          vodStatus: vod?.status || 'UNKNOWN',
        }
      }

      if (vod.status !== 'VOD_STATUS_SUCCEEDED') return video

      const { token } = await createPlaybackToken({
        resourceId: video.vodId,
        resourceType: 'RESOURCE_TYPE_VOD_EVENT',
      })
      const coverImage = vod?.cover_image?.auto?.url

      return {
        ...video,
        isConverted: true,
        vodStatus: vod.status,
        ...(coverImage ? { coverImage } : {}),
        ...(token ? { playbackToken: token } : {}),
      }
    }),
  )
}

const usePollTrailerViewBlendVisionVodStatus = (videos: TrailerViewVideoData[]) => {
  const shouldPoll = videos.some(
    (v) => v.vodId && !v.isConverted && !isVODStatusFailed(v.vodStatus),
  )

  const { data: polledVideos } = useSWR(
    shouldPoll ? ['vod-status-polling', videos] : null,
    () => fetchVodStatus(videos),
    {
      refreshInterval: shouldPoll ? POLLING_INTERVAL_MS : 0,
      revalidateOnFocus: false,
      fallbackData: videos,
    },
  )

  return polledVideos
}

export default usePollTrailerViewBlendVisionVodStatus
